import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";

// ============================================================================
// Types — alineados con app/schemas/sources.py
// ============================================================================

export interface SourceOut {
  id: string;
  project_id: string;
  kind: string;
  site_id: string;
  drive_id: string;
  folder_path: string | null;
  display_name: string | null;
  enabled: boolean;
  last_synced_at: string | null;
  last_sync_error: string | null;
  created_at: string;
  doc_count: number;
}

export interface SourceCreate {
  // Forma cómoda: pegar la URL de la biblioteca/carpeta de SharePoint.
  sharepoint_url?: string;
  // Forma explícita (alternativa): site_id + drive_id (+ folder_path).
  site_id?: string;
  drive_id?: string;
  folder_path?: string;
  display_name?: string;
}

export interface SyncResult {
  creados: number;
  actualizados: number;
  borrados: number;
  omitidos: number;
  errores: number;
}

// ============================================================================
// Query Keys
// ============================================================================

export const sourceKeys = {
  all: ["sources"] as const,
  list: (projectId: string) => [...sourceKeys.all, "list", projectId] as const,
};

// ============================================================================
// Hooks
// ============================================================================

export function useSources(projectId: string) {
  return useQuery<SourceOut[], Error>({
    queryKey: sourceKeys.list(projectId),
    queryFn: () => fetchWithAuth(`/api/v1/projects/${projectId}/sources`),
    enabled: !!projectId,
    // Auto-refresh: el nº de documentos crece según el worker procesa lo que
    // trae la sincronización; refrescamos mientras la pestaña está abierta.
    refetchInterval: 10000,
  });
}

export function useCreateSource(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<SourceOut, Error, SourceCreate>({
    mutationFn: (payload) =>
      fetchWithAuth(`/api/v1/projects/${projectId}/sources`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.list(projectId) });
      showToast.success("Fuente de SharePoint conectada");
    },
    onError: (e) =>
      showToast.error(e.message || "No se pudo conectar la fuente"),
  });
}

export interface SourceUpdate {
  display_name?: string | null;
  enabled?: boolean;
}

export function useUpdateSource(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<SourceOut, Error, { id: string; data: SourceUpdate }>({
    mutationFn: ({ id, data }) =>
      fetchWithAuth(`/api/v1/projects/${projectId}/sources/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.list(projectId) });
      showToast.success("Fuente actualizada");
    },
    onError: (e) => showToast.error(e.message || "No se pudo actualizar la fuente"),
  });
}

/** Lanza una sincronización manual (delta) de la fuente. */
export function useSyncSource(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<SyncResult, Error, string>({
    mutationFn: (sourceId) =>
      fetchWithAuth(
        `/api/v1/projects/${projectId}/sources/${sourceId}/sync`,
        { method: "POST" }
      ),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.list(projectId) });
      // Los documentos nuevos aparecen en la pestaña Documentos procesándose.
      queryClient.invalidateQueries({ queryKey: ["documents", "list", projectId] });
      if (r.errores > 0) {
        showToast.error(
          `Sincronización con ${r.errores} error(es). Revisa el detalle de la fuente.`
        );
      } else {
        showToast.success(
          `Sincronizado: ${r.creados} nuevos · ${r.actualizados} actualizados · ${r.borrados} borrados`
        );
      }
    },
    onError: (e) => showToast.error(e.message || "Error al sincronizar"),
  });
}

export function useDeleteSource(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (sourceId) =>
      fetchWithAuth(`/api/v1/projects/${projectId}/sources/${sourceId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.list(projectId) });
      showToast.success("Fuente eliminada");
    },
    onError: (e) => showToast.error(e.message || "Error al eliminar la fuente"),
  });
}
